// workout.jsx — WeightRepInput (combined weight + reps keypad input, used by the
// review/summary edit sheet) and WorkoutPreviewScreen (read-only day preview).
// The live workout lives in active.jsx and the summary/review in summary.jsx.

import React from 'react';
import { fmtWeight, unitLabel, useApp } from './store.jsx';
import { tryVibrate } from './timers.jsx';
import { LL, LLIcon, LLKicker } from './tokens.jsx';
import { MuscleThumb } from './musclemap.jsx';
import { PageHeader } from './phone.jsx';
import { prescribe, programWeekInfo, rowsSummary, schemeOf } from './progression.js';
import { parseWeightInput, prescriptionFor, nudgeReps, sliderMaxFor, weightIncrementFor } from './utils.jsx';

// ———————————————————————————————————————————————————————————————
// Combined weight + reps input widget (INPUT-01..04)
// ———————————————————————————————————————————————————————————————
function _WeightRepInput({ w, reps, pe, units, haptics, onChange, autoFocus }) {
  const [localW, setLocalW] = React.useState('');
  const [localReps, setLocalReps] = React.useState('');
  const [isFocusedW, setIsFocusedW] = React.useState(false);
  const [isFocusedReps, setIsFocusedReps] = React.useState(false);

  const [sliderMax, setSliderMax] = React.useState(() => sliderMaxFor(w));
  const inc = weightIncrementFor(pe);
  const prevSliderStep = React.useRef(null);

  // Press-and-hold auto-repeat refs
  const holdTimer = React.useRef(null);
  const holdInterval = React.useRef(null);

  // Stable action refs — updated each render so auto-repeat always uses the
  // latest w/reps/pe values, not the stale closure from the first pointerdown.
  const incWRef = React.useRef(null);
  const decWRef = React.useRef(null);
  const incRRef = React.useRef(null);
  const decRRef = React.useRef(null);
  // Steps land on the loadable grid of the display unit (2.5 kg / 5 lb …):
  // an off-grid value (61.2 kg) snaps to the next grid point in the step's
  // direction instead of producing 63.7, 66.2, …
  const KGF = 0.45359237;
  const gridStep = (dir) => {
    const incLb = pe && pe.incLb ? pe.incLb : inc;
    const kg = units === 'kg';
    const v = kg ? (w || 0) * KGF : (w || 0);
    const st = kg ? Math.round(incLb * KGF * 100) / 100 : incLb;
    const k = v / st;
    const next = dir > 0 ? (Math.floor(k + 1e-3) + 1) * st : Math.max(0, (Math.ceil(k - 1e-3) - 1) * st);
    const r = Math.round(next * 100) / 100;
    return kg ? +(r / KGF).toFixed(4) : r;
  };
  incWRef.current = () => onChange({ w: gridStep(1) });
  decWRef.current = () => onChange({ w: gridStep(-1) });
  incRRef.current = () => onChange({ reps: nudgeReps(reps, '+') });
  decRRef.current = () => onChange({ reps: nudgeReps(reps, '-') });

  // Inject .ll-slider CSS once (idempotent)
  React.useEffect(() => {
    if (document.getElementById('ll-slider-style')) return;
    const el = document.createElement('style');
    el.id = 'll-slider-style';
    el.textContent = [
      '.ll-slider { -webkit-appearance: none; appearance: none;',
      '  width: 100%; height: 4px; background: var(--ll-line2, oklch(0.42 0.008 250));',
      '  border-radius: 2px; outline: none; margin: 12px 0; cursor: pointer;',
      '  touch-action: none; }',
      '.ll-slider::-webkit-slider-thumb { -webkit-appearance: none;',
      '  width: 20px; height: 20px; border-radius: 50%;',
      '  background: var(--ll-accent, oklch(0.86 0.19 132));',
      '  border: 2px solid var(--ll-surface, oklch(0.22 0.005 250));',
      '  box-shadow: 0 1px 4px rgba(0,0,0,0.4); cursor: grab; }',
      '.ll-slider::-webkit-slider-thumb:active { cursor: grabbing; transform: scale(1.15); }',
      '.ll-slider::-moz-range-thumb { width: 20px; height: 20px; border-radius: 50%;',
      '  background: var(--ll-accent, oklch(0.86 0.19 132));',
      '  border: 2px solid var(--ll-surface, oklch(0.22 0.005 250)); cursor: grab; }',
      '.ll-slider:focus-visible { outline: 2px solid var(--ll-accent, oklch(0.86 0.19 132));',
      '  outline-offset: 4px; }',
    ].join('\n');
    document.head.appendChild(el);
  }, []);

  // Cleanup on unmount: cancel any live hold timer/interval so a backdrop-tap
  // during a long-press cannot fire into the closed (null) editSheet state.
  React.useEffect(() => () => clearHold(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearHold = () => {
    clearTimeout(holdTimer.current);
    clearInterval(holdInterval.current);
  };

  // startHold: arm the hold-detection timer only. The immediate single-tap
  // action is handled by onClick (fires on mouse click AND keyboard Space/Enter),
  // satisfying WCAG 2.1 SC 2.1.1 (Keyboard). onPointerDown starts the 500 ms
  // hold window; onPointerUp/Leave/Cancel disarms it via clearHold().
  // A hold that auto-repeated swallows the click that follows pointer-up, so
  // releasing doesn't add one extra step.
  const held = React.useRef(false);
  const startHold = (fnRef) => () => {
    clearHold();
    held.current = false;
    holdTimer.current = setTimeout(() => {
      holdInterval.current = setInterval(() => { held.current = true; fnRef.current(); }, 100);
    }, 500);
  };
  const tap = (fnRef) => () => { if (held.current) { held.current = false; return; } fnRef.current(); };

  // Weight field handlers
  // On focus: seed with the display-unit value the user already sees (same
  // factor as fmtWeight: lb × 0.45359237 → kg). lb mode seeds raw lb unchanged.
  const wSeed = React.useRef('');
  const handleWFocus = () => {
    const seed = units === 'kg' ? fmtWeight(w, 'kg') : String(w);
    wSeed.current = seed;
    setLocalW(seed);
    setIsFocusedW(true);
  };
  const handleWBlur = () => {
    setIsFocusedW(false);
    // Focus + blur without typing must not rewrite the stored value (the kg
    // display is rounded; converting it back would drift 135 lb → 134.92).
    if (localW.trim() === wSeed.current.trim()) return;
    const v = parseWeightInput(localW);
    if (v !== null) {
      // v is in display units; convert back to lb (storage unit, DEC-weight-storage-lb).
      // Inverse of fmtWeight's lb→kg factor (0.45359237): kg ÷ 0.45359237 = lb.
      // lb mode: v is already lb, no conversion needed.
      const lb = units === 'kg' ? +(v / 0.45359237).toFixed(4) : v;
      onChange({ w: lb });
      setSliderMax(sliderMaxFor(lb));
    }
    // else: silent revert — invalid string leaves parent w unchanged
  };

  // Reps field handlers
  const handleRFocus = () => { setLocalReps(String(reps)); setIsFocusedReps(true); };
  const handleRBlur = () => {
    setIsFocusedReps(false);
    const v = parseWeightInput(localReps); // reuse tested parser, then integer-clamp
    if (v !== null) onChange({ reps: Math.max(1, Math.round(v)) });
    // else: silent revert
  };

  // Slider handlers (INPUT-02)
  const handleSliderInput = (e) => {
    const newW = parseFloat(e.target.value);
    if (prevSliderStep.current === null) prevSliderStep.current = newW;
    if (newW !== prevSliderStep.current) {
      if (haptics) tryVibrate(8);
      prevSliderStep.current = newW;
    }
    onChange({ w: newW });
  };
  // Border color for weight field
  const wIsInvalid = isFocusedW && localW.trim() !== '' && parseWeightInput(localW) === null;
  const wBorder = isFocusedW
    ? (wIsInvalid ? `1.5px solid ${LL.danger}` : `1.5px solid ${LL.accent}`)
    : `1px solid ${LL.line}`;

  // Border color for reps field
  const rIsInvalid = isFocusedReps && localReps.trim() !== '' && parseWeightInput(localReps) === null;
  const rBorder = isFocusedReps
    ? (rIsInvalid ? `1.5px solid ${LL.danger}` : `1.5px solid ${LL.accent}`)
    : `1px solid ${LL.line}`;

  // Slider thumb snaps to increment for rendering; does NOT retroactively snap typed values
  const sliderDisplayW = Math.min(Math.round(w / inc) * inc, sliderMax);

  const stepperBtn = (atBound) => ({
    width: 44, height: 44, borderRadius: LL.r.md,
    background: LL.surface, border: `1px solid ${LL.line2}`,
    color: atBound ? LL.fgMute : LL.fg,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 0, cursor: 'pointer', flexShrink: 0,
    touchAction: 'manipulation',
  });

  const blockStyle = {
    background: LL.surface2, borderRadius: LL.r.md,
    border: `1px solid ${LL.line}`, padding: 12, marginBottom: 10,
  };

  const fieldStyle = (border) => ({
    width: '100%', height: 56, paddingLeft: 12, paddingRight: 36,
    textAlign: 'center', fontFamily: LL.display, fontSize: LL.fs.h1,
    fontWeight: 700, fontVariantNumeric: 'tabular-nums', letterSpacing: -0.5,
    color: LL.fg, background: LL.bg, border, borderRadius: LL.r.sm,
    outline: 'none', boxSizing: 'border-box',
  });

  const valueRow = { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 };

  return (
    <div>
      {/* ——— WEIGHT block ——— */}
      <div style={blockStyle}>
        <LLKicker>WEIGHT</LLKicker>
        <div style={valueRow}>
          <button
            aria-label="Decrease weight"
            aria-disabled={w === 0 ? 'true' : 'false'}
            style={stepperBtn(w === 0)}
            onClick={tap(decWRef)}
            onPointerDown={startHold(decWRef)}
            onPointerUp={clearHold}
            onPointerLeave={clearHold}
            onPointerCancel={clearHold}
          ><LLIcon.minus /></button>
          <div style={{ flex: 1, position: 'relative' }}>
            <input
              type="text"
              inputMode="decimal"
              aria-label={`Weight in ${unitLabel(units)}`}
              value={isFocusedW ? localW : fmtWeight(w, units)}
              onFocus={handleWFocus}
              onChange={e => setLocalW(e.target.value)}
              onBlur={handleWBlur}
              style={fieldStyle(wBorder)}
            />
            <span style={{
              position: 'absolute', right: 10, top: '50%',
              transform: 'translateY(-50%)', fontFamily: LL.ui, fontSize: LL.fs.meta,
              fontWeight: 500, color: LL.fgDim, textTransform: 'uppercase',
              pointerEvents: 'none', userSelect: 'none',
            }}>{unitLabel(units)}</span>
          </div>
          <button
            aria-label="Increase weight"
            style={stepperBtn(false)}
            onClick={tap(incWRef)}
            onPointerDown={startHold(incWRef)}
            onPointerUp={clearHold}
            onPointerLeave={clearHold}
            onPointerCancel={clearHold}
          ><LLIcon.plus /></button>
        </div>
      </div>

      {/* ——— REPS block (no slider — INPUT-04) ——— */}
      <div style={blockStyle}>
        <LLKicker>REPS</LLKicker>
        <div style={valueRow}>
          <button
            aria-label="Decrease reps"
            aria-disabled={reps === 1 ? 'true' : 'false'}
            style={stepperBtn(reps === 1)}
            onClick={tap(decRRef)}
            onPointerDown={startHold(decRRef)}
            onPointerUp={clearHold}
            onPointerLeave={clearHold}
            onPointerCancel={clearHold}
          ><LLIcon.minus /></button>
          <div style={{ flex: 1, position: 'relative' }}>
            <input
              type="text"
              inputMode="numeric"
              aria-label="Reps"
              value={isFocusedReps ? localReps : String(reps)}
              onFocus={handleRFocus}
              onChange={e => setLocalReps(e.target.value)}
              onBlur={handleRBlur}
              style={{ ...fieldStyle(rBorder), paddingRight: 12 }}
            />
          </div>
          <button
            aria-label="Increase reps"
            style={stepperBtn(false)}
            onClick={tap(incRRef)}
            onPointerDown={startHold(incRRef)}
            onPointerUp={clearHold}
            onPointerLeave={clearHold}
            onPointerCancel={clearHold}
          ><LLIcon.plus /></button>
        </div>
      </div>
    </div>
  );
}

// ———————————————————————————————————————————————————————————————
// ExerciseSummaryCard — collapsed state: name, sets-done, stats, trend

// ———————————————————————————————————————————————————————————————
// WorkoutPreviewScreen — strictly read-only prescribed-exercise list
// REVIEW-03: no Start button, no log control, no route to active-workout
// ———————————————————————————————————————————————————————————————
function WorkoutPreviewScreen({ dayId, date }) {
  const { state, goBack, setRoute } = useApp();
  const units = state.prefs.units;
  const prog  = state.programs.find(p => p.days.some(d => d.id === dayId));
  const day   = prog?.days.find(d => d.id === dayId);
  const exById = React.useMemo(() => Object.fromEntries(state.exercises.map(e => [e.id, e])), [state.exercises]);

  if (!prog || !day) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: LL.bg, alignItems: 'center', justifyContent: 'center', padding: 30, textAlign: 'center', gap: 14 }}>
        <div style={{ fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, color: LL.fg }}>Workout not found</div>
        <button onClick={goBack} style={{ height: 44, padding: '0 18px', borderRadius: LL.r.md, background: LL.surface, color: LL.fg, border: `1px solid ${LL.line}`, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, cursor: 'pointer' }}>Back to home</button>
      </div>
    );
  }

  // MANDATORY spread: ...pe first so pe.targetWeight survives the prescriptionFor overlay
  const resolved = (day.exercises ?? []).map(pe => ({ ...pe, ...prescriptionFor(prog, pe) }));
  const mins = Math.max(10, Math.round(resolved.reduce((a, re) => a + (re.sets || 3) * (45 + (re.rest || 120)), 0) / 60 / 5) * 5);
  const when = date ? new Date(date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' }) : (day.day || '');
  // Engine schemes (waves, top sets, meso) show this week's prescription.
  const wk = programWeekInfo({ program: prog, sessions: state.sessions });
  const hasWeeks = !!prog.cycle;
  const plan = (pe) => (schemeOf(pe) === 'double' ? null : prescribe({ program: prog, pe, weekIndex: wk.index, units, history: state.sessions }));
  const start = () => {
    const live = state.sessions.find(s => s.id === state.activeSessionId && !s.completedAt);
    setRoute('active-workout', { dayId: live && live.dayId !== dayId ? live.dayId : dayId });
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: LL.bg, position: 'relative' }}>
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 120 }}>
        <PageHeader onBack={goBack} kicker={prog.name} title={day.name} style={{ paddingBottom: 4, alignItems: 'flex-start' }}
          titleStyle={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }} />
        <div style={{ padding: '0 16px 14px 68px', fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, fontVariantNumeric: 'tabular-nums' }}>
          {[when, hasWeeks ? (wk.deload ? `Week ${wk.week + 1} · deload` : String(wk.label || '')) : null, `${resolved.length} exercise${resolved.length === 1 ? '' : 's'}`, `~${mins} min`].filter(Boolean).join(' · ')}
        </div>
        <div style={{ padding: '0 16px' }}>
          {resolved.length === 0 ? (
            <div style={{ padding: 20, borderRadius: LL.r.md, background: LL.surface, border: `1px dashed ${LL.line2}`, color: LL.fgMute, fontFamily: LL.ui, fontSize: LL.fs.meta, textAlign: 'center' }}>
              No exercises programmed for this day yet.
            </div>
          ) : (
            <div style={{ background: LL.surface, border: `1px solid ${LL.line}`, borderRadius: LL.r.md, overflow: 'hidden' }}>
              {resolved.map((re, i) => {
                const ex = exById[re.exerciseId] || {};
                const reps = re.repMin === re.repMax ? `${re.repMin}` : `${re.repMin}–${re.repMax}`;
                const rx = plan(re);
                return (
                  <button key={re.id ?? i} onClick={() => re.exerciseId && setRoute('exercise', { exerciseId: re.exerciseId })} style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'none', border: 'none',
                    borderTop: i ? `1px solid ${LL.line}` : 'none', cursor: 'pointer', textAlign: 'left',
                  }}>
                    <span style={{ width: 48, height: 48, borderRadius: LL.r.md, background: LL.surface2, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                      <MuscleThumb primary={ex.primary || [re.group || ex.group]} secondary={ex.secondary || []} size={48} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg, lineHeight: 1.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}>{re.name || ex.name || 'Exercise'}</div>
                      <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginTop: 2, fontVariantNumeric: 'tabular-nums', lineHeight: 1.4 }}>
                        {rx ? rowsSummary(rx.rows, units, { weights: !rx.needsTM }) : <>{re.sets} × {reps}{re.targetWeight > 0 ? ` · ${fmtWeight(re.targetWeight, units)} ${unitLabel(units)}` : ''}</>}{re.rest ? ` · rest ${Math.floor(re.rest / 60)}:${String(re.rest % 60).padStart(2, '0')}` : ''}
                      </div>
                    </span>
                    <LLIcon.chev style={{ color: LL.fgFaint, flexShrink: 0 }} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {resolved.length > 0 && (
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '24px 16px calc(env(safe-area-inset-bottom, 0px) + 16px)', background: `linear-gradient(to top, ${LL.bg} 60%, color-mix(in oklab, ${LL.bg} 0%, transparent))` }}>
          <button onClick={start} style={{
            width: '100%', height: 56, borderRadius: LL.r.md, border: 'none', background: LL.accent, color: LL.accentInk,
            fontFamily: LL.ui, fontSize: LL.fs.btn, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}><LLIcon.play style={{ width: 18, height: 18 }} /> Start {day.name}</button>
        </div>
      )}
    </div>
  );
}

export { WorkoutPreviewScreen, _WeightRepInput as WeightRepInput };
